import { BellRing, Send, Users } from "lucide-react";
import { CancelInterviewButton } from "../../../components/interviewList/CancelInterviewButton";
import {
  CANDIDATE_STATUS_CLASSES,
  CANDIDATE_STATUS_LABELS,
  CANDIDATE_STATUS_UNSENT,
} from "../../../components/interviewList/candidateStatus";
import {
  formatInterviewDate,
  formatInterviewTime,
} from "../../../components/interviewList/schedule";
import { CandidateStatus } from "../../../graphql/generated/graphql";
import {
  elapsedDaysSinceRequest,
  isRemindableRespondent,
  isSendableRespondent,
  type StakeholderRespondent,
} from "../../../hooks";

interface StakeholderRespondentSectionProps {
  stakeholderRespondents: readonly StakeholderRespondent[];
  loading: boolean;
  sending: boolean;
  onSendRequest: (respondent: StakeholderRespondent) => void;
  onBulkSendRequest: () => void;
  onBulkSendReminder: () => void;
  onCancelled: () => void;
}

function EmptyCell() {
  return <span className="text-[#9ca3af] text-[13px]">-</span>;
}

function RequestedAtCell({ requestedAt }: { requestedAt: string | null }) {
  const elapsedDays = elapsedDaysSinceRequest(requestedAt);
  if (!requestedAt || elapsedDays === null) {
    return <EmptyCell />;
  }
  return (
    <>
      <div className="text-[var(--color-text-primary)]">
        {formatInterviewDate(requestedAt)}
      </div>
      <div className="text-[13px] text-[var(--color-text-secondary)]">
        依頼から{elapsedDays}日
      </div>
    </>
  );
}

export function StakeholderRespondentSection({
  stakeholderRespondents,
  loading,
  sending,
  onSendRequest,
  onBulkSendRequest,
  onBulkSendReminder,
  onCancelled,
}: StakeholderRespondentSectionProps) {
  const sendableCount =
    stakeholderRespondents.filter(isSendableRespondent).length;
  const remindableCount = stakeholderRespondents.filter(
    isRemindableRespondent,
  ).length;

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <div>
          <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
            回答依頼
          </h2>
          <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
            回答者ごとに1件。依頼メールのリンクから回答者自身が日程を予約します
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBulkSendReminder}
            disabled={remindableCount === 0 || sending}
            className={`flex items-center gap-1.5 rounded-md border px-4 py-2 font-medium text-sm ${
              remindableCount === 0 || sending
                ? "cursor-not-allowed border-[var(--color-border-default)] text-[#9ca3af]"
                : "cursor-pointer border-[var(--color-primary-500)] text-[var(--color-primary-500)] hover:bg-[var(--color-bg-subtle-gray)]"
            }`}
          >
            <BellRing className="h-4 w-4" />
            未予約者にリマインド
          </button>
          <button
            type="button"
            onClick={onBulkSendRequest}
            disabled={sendableCount === 0 || sending}
            className={`flex items-center gap-1.5 rounded-md border-none px-4 py-2 font-medium text-sm text-white ${
              sendableCount === 0 || sending
                ? "cursor-not-allowed bg-[#9ca3af]"
                : "cursor-pointer bg-[var(--color-primary-500)] hover:bg-[#045a94]"
            }`}
          >
            <Send className="h-4 w-4" />
            未送信者に一括送信
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-12 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      ) : stakeholderRespondents.length === 0 ? (
        <div className="p-12 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
          <p className="text-[var(--color-text-secondary)]">
            回答者がまだいません。先に関係性を入稿してください
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-subtle-gray)]">
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  回答者
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  受け取る人
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  依頼日
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  確定日時
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  インタビュアー
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  予約ページ
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  状態
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {stakeholderRespondents.map((respondent) => (
                <tr
                  key={respondent.candidateId}
                  className="border-[var(--color-border-default)] border-b"
                >
                  <td className="px-4 py-4 text-sm">
                    <div className="font-medium text-[var(--color-text-primary)]">
                      {respondent.respondentName}
                    </div>
                    <div className="text-[13px] text-[var(--color-text-secondary)]">
                      {respondent.respondentEmail}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {respondent.recipientNames.length === 0 ? (
                      <span className="text-[#b45309] text-[13px]">
                        関係性が未入稿のため送信できません
                      </span>
                    ) : (
                      <div className="text-[var(--color-text-primary)]">
                        {respondent.recipientNames.join("、")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <RequestedAtCell requestedAt={respondent.requestedAt} />
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {respondent.interviewStartTime ? (
                      <>
                        <div className="text-[var(--color-text-primary)]">
                          {formatInterviewDate(respondent.interviewStartTime)}
                        </div>
                        <div className="text-[13px] text-[var(--color-text-secondary)]">
                          {formatInterviewTime(respondent.interviewStartTime)}
                        </div>
                      </>
                    ) : (
                      <EmptyCell />
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {respondent.interviewerName ? (
                      <span className="text-[var(--color-text-primary)]">
                        {respondent.interviewerName}
                      </span>
                    ) : (
                      <EmptyCell />
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <a
                      href={respondent.scheduleUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--color-primary-500)] hover:underline"
                    >
                      リンク
                    </a>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <span
                      className={`whitespace-nowrap rounded-full px-2.5 py-1 font-medium text-[12px] ${
                        CANDIDATE_STATUS_CLASSES[respondent.status] ??
                        CANDIDATE_STATUS_CLASSES[CANDIDATE_STATUS_UNSENT]
                      }`}
                    >
                      {CANDIDATE_STATUS_LABELS[respondent.status] ??
                        respondent.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {respondent.status === CandidateStatus.Unsent ? (
                      <button
                        type="button"
                        onClick={() => onSendRequest(respondent)}
                        disabled={!isSendableRespondent(respondent) || sending}
                        className={`border-none bg-none p-0 ${
                          !isSendableRespondent(respondent) || sending
                            ? "cursor-not-allowed text-[#9ca3af]"
                            : "cursor-pointer text-[var(--color-primary-500)] hover:underline"
                        }`}
                      >
                        依頼を送信
                      </button>
                    ) : respondent.status === CandidateStatus.Booked &&
                      respondent.interviewId ? (
                      <CancelInterviewButton
                        interviewId={respondent.interviewId}
                        candidateName={respondent.respondentName}
                        onCancelled={onCancelled}
                      />
                    ) : (
                      <span className="text-[#9ca3af] text-[13px]">
                        送信済みのため再送はできません
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
